/**
 * CompareAllCharts.jsx : graphiques de la comparaison, une carte par mesure.
 *
 * MetricChartCard : titre, graphique (ou mention « non mesuré ») et légende des
 * seuils d'une mesure ; sert l'onglet d'une mesure et la pile ci-dessous.
 *
 * CompareAllCharts : onglet « Tous les paramètres » (D257), un graphique par
 * mesure visible, empilés dans l'ordre de COMPARE_METRICS comme le PDF, même
 * alignement et même fenêtre d'analyse. Un zoom ou un déplacement sur l'un est
 * appliqué aux autres (view ; sur sa source, la vue est déjà celle-là) ; la vue repart de
 * la fenêtre d'analyse au changement d'alignement ou de plage. Pas d'axe Y
 * multiple : chaque mesure garde son unité et ses seuils.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CompareChart from '../charts/CompareChart';
import { ThresholdLegend } from './_compareBlocks';
import { formatNumberLocale } from '../utils/formatters';
import { metricUnit, thresholdLines, anySessionMeasures } from './compareMetrics';

const STACKED_CHART_HEIGHT = 220;

/** Libellé, unité et lignes de seuil traduits d'une mesure. */
export function metricChartConfig(metric, tr) {
  const unit = metricUnit(metric, tr);
  return {
    label: tr(metric.labelKey),
    unit,
    thresholds: thresholdLines(metric).map(({ kind, value, color }) => ({
      value, color, unit, label: tr(`sessions.compare.threshold.${kind}`, { v: formatNumberLocale(value, 0) }),
    })),
  };
}

export function MetricChartCard({ loaded, metric, alignMode, darkMode, analysisWindow, onAnalyzeZoom,
  card, c, chartHeight, view, onViewChange }) {
  const { t: tr } = useTranslation();
  // Seuils stables d'un rendu à l'autre : un tableau neuf recréerait les options
  // du graphique et annulerait son zoom (CompareChart).
  const cfg = useMemo(() => metricChartConfig(metric, tr), [metric, tr]);
  const measured = anySessionMeasures(loaded, metric.field);
  return (
    <div style={card} data-testid={`compare-chart-${metric.field}`}>
      <h3 style={{ fontFamily: "'Poppins', sans-serif", fontSize: '15px', fontWeight: '700',
        color: c.text, margin: '0 0 16px' }}>
        {tr('sessions.compare.chartTitle', { label: cfg.label, unit: cfg.unit })}
      </h3>
      {/* Aucune session ne porte la mesure : mention à la place d'un graphique vide. */}
      {!measured && (
        <p data-testid="compare-metric-not-measured"
          style={{ fontSize: '13px', color: c.textSec, textAlign: 'center', padding: '40px 0', margin: 0 }}>
          {tr(metric.notMeasuredKey)}
        </p>
      )}
      {measured && (
        <CompareChart
          sessions={loaded}
          field={metric.field}
          yLabel={tr('sessions.compare.yLabel', { label: cfg.label, unit: cfg.unit })}
          thresholds={cfg.thresholds}
          alignMode={alignMode}
          darkMode={darkMode}
          analysisWindow={analysisWindow}
          onAnalyzeZoom={onAnalyzeZoom}
          view={view}
          onViewChange={onViewChange}
          {...(chartHeight ? { chartHeight } : {})}
        />
      )}
      {measured && <ThresholdLegend thresholds={cfg.thresholds} c={c} />}
    </div>
  );
}

export default function CompareAllCharts({ loaded, metrics, alignMode, darkMode, analysisWindow, onAnalyzeZoom, card, c }) {
  // { min, max } : vue du dernier graphique zoomé ou déplacé, null = fenêtre d'analyse.
  const [view, setView] = useState(null);
  const windowKey = analysisWindow ? `${analysisWindow.min}:${analysisWindow.max}` : 'full';
  useEffect(() => { setView(null); }, [alignMode, windowKey]);

  return metrics.map(metric => (
    <MetricChartCard key={metric.field} loaded={loaded} metric={metric} alignMode={alignMode}
      darkMode={darkMode} analysisWindow={analysisWindow} onAnalyzeZoom={onAnalyzeZoom}
      card={card} c={c} chartHeight={STACKED_CHART_HEIGHT}
      view={view} onViewChange={setView} />
  ));
}
